/**
 * SEO Utilities for AI Notes Post Editor
 * Provides real-time SEO scoring and actionable suggestions
 */

// Ideal ranges for SEO
const SEO_RULES = {
  title: {
    minLength: 30,
    maxLength: 60,
    idealLength: 55
  },
  metaTitle: {
    minLength: 30,
    maxLength: 70,
    idealLength: 55
  },
  metaDescription: {
    minLength: 120,
    maxLength: 160,
    idealLength: 155
  },
  content: {
    minWords: 300,
    idealWords: 1000,
    excellentWords: 1500
  },
  slug: {
    maxLength: 75,
    pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/
  }
};

/**
 * Count words in content (strips HTML)
 * @param {string} html - HTML content
 * @returns {number} Word count
 */
export const countWords = (html) => {
  if (!html) return 0;
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Check a single SEO criterion
 * @returns {Object} { pass: boolean, score: number, message: string, suggestion: string }
 */
const checkCriterion = (value, rule, name) => {
  if (!value) {
    return {
      pass: false,
      score: 0,
      status: 'missing',
      message: `${name} is missing`,
      suggestion: `Add a ${name.toLowerCase()}`
    };
  }
  
  const length = typeof value === 'string' ? value.length : value;
  
  if (rule.minLength && length < rule.minLength) {
    return {
      pass: false,
      score: 25,
      status: 'short',
      message: `${name} is too short (${length}/${rule.minLength} min)`,
      suggestion: `Add ${rule.minLength - length} more characters`
    };
  }
  
  if (rule.maxLength && length > rule.maxLength) {
    return {
      pass: false,
      score: 50,
      status: 'long',
      message: `${name} is too long (${length}/${rule.maxLength} max)`,
      suggestion: `Remove ${length - rule.maxLength} characters`
    };
  }
  
  return {
    pass: true,
    score: 100,
    status: 'good',
    message: `${name} length is good (${length} chars)`,
    suggestion: null
  };
};

/**
 * Calculate comprehensive SEO score for a post
 * @param {Object} post - Post data
 * @returns {Object} SEO analysis results
 */
export const calculateSeoScore = (post) => {
  const checks = [];
  let totalScore = 0;
  let maxScore = 0;
  
  // 1. Title Check (weight: 15)
  const titleCheck = checkCriterion(post.title, SEO_RULES.title, 'Title');
  checks.push({ ...titleCheck, name: 'Title', weight: 15 });
  totalScore += (titleCheck.score / 100) * 15;
  maxScore += 15;
  
  // 2. Meta Title Check (weight: 10)
  const metaTitleCheck = checkCriterion(post.meta_title, SEO_RULES.metaTitle, 'Meta Title');
  checks.push({ ...metaTitleCheck, name: 'Meta Title', weight: 10 });
  totalScore += (metaTitleCheck.score / 100) * 10;
  maxScore += 10;
  
  // 3. Meta Description Check (weight: 15)
  const metaDescCheck = checkCriterion(post.meta_description, SEO_RULES.metaDescription, 'Meta Description');
  checks.push({ ...metaDescCheck, name: 'Meta Description', weight: 15 });
  totalScore += (metaDescCheck.score / 100) * 15;
  maxScore += 15;
  
  // 4. Content Length Check (weight: 20)
  const wordCount = countWords(post.content);
  let contentCheck;
  if (wordCount < SEO_RULES.content.minWords) {
    contentCheck = {
      pass: false,
      score: Math.min(75, (wordCount / SEO_RULES.content.minWords) * 75),
      status: 'short',
      message: `Content is short (${wordCount}/${SEO_RULES.content.minWords} min words)`,
      suggestion: `Add ${SEO_RULES.content.minWords - wordCount} more words`
    };
  } else if (wordCount >= SEO_RULES.content.excellentWords) {
    contentCheck = {
      pass: true,
      score: 100,
      status: 'excellent',
      message: `Excellent content length (${wordCount} words)`,
      suggestion: null
    };
  } else if (wordCount >= SEO_RULES.content.idealWords) {
    contentCheck = {
      pass: true,
      score: 100,
      status: 'good',
      message: `Good content length (${wordCount} words)`,
      suggestion: null
    };
  } else {
    contentCheck = {
      pass: true,
      score: 85,
      status: 'okay',
      message: `Content length acceptable (${wordCount} words)`,
      suggestion: `Consider adding more content (${SEO_RULES.content.idealWords}+ words is ideal)`
    };
  }
  checks.push({ ...contentCheck, name: 'Content Length', weight: 20 });
  totalScore += (contentCheck.score / 100) * 20;
  maxScore += 20;
  
  // 5. Featured Image Check (weight: 15)
  const hasImage = !!post.image_id || !!post.image;
  const imageCheck = {
    pass: hasImage,
    score: hasImage ? 100 : 0,
    status: hasImage ? 'good' : 'missing',
    message: hasImage ? 'Featured image is set' : 'No featured image',
    suggestion: hasImage ? null : 'Add a featured image for better social sharing'
  };
  checks.push({ ...imageCheck, name: 'Featured Image', weight: 15 });
  totalScore += (imageCheck.score / 100) * 15;
  maxScore += 15;
  
  // 6. Slug Format Check (weight: 10)
  const slug = post.slug || '';
  let slugCheck;
  if (!slug) {
    slugCheck = {
      pass: false,
      score: 0,
      status: 'missing',
      message: 'URL slug is missing',
      suggestion: 'Add a URL slug'
    };
  } else if (!SEO_RULES.slug.pattern.test(slug)) {
    slugCheck = {
      pass: false,
      score: 50,
      status: 'invalid',
      message: 'URL slug contains invalid characters',
      suggestion: 'Use only lowercase letters, numbers, and hyphens'
    };
  } else if (slug.length > SEO_RULES.slug.maxLength) {
    slugCheck = {
      pass: false,
      score: 75,
      status: 'long',
      message: `URL slug is too long (${slug.length}/${SEO_RULES.slug.maxLength})`,
      suggestion: 'Shorten the URL slug'
    };
  } else {
    slugCheck = {
      pass: true,
      score: 100,
      status: 'good',
      message: 'URL slug format is good',
      suggestion: null
    };
  }
  checks.push({ ...slugCheck, name: 'URL Slug', weight: 10 });
  totalScore += (slugCheck.score / 100) * 10;
  maxScore += 10;
  
  // 7. Tags Check (weight: 10)
  const tags = post.tags || [];
  let tagsCheck;
  if (tags.length === 0) {
    tagsCheck = {
      pass: false,
      score: 0,
      status: 'missing',
      message: 'No tags added',
      suggestion: 'Add 1-5 relevant tags'
    };
  } else if (tags.length > 5) {
    tagsCheck = {
      pass: true,
      score: 75,
      status: 'many',
      message: `Many tags (${tags.length})`,
      suggestion: 'Consider reducing to 3-5 tags'
    };
  } else {
    tagsCheck = {
      pass: true,
      score: 100,
      status: 'good',
      message: `Good number of tags (${tags.length})`,
      suggestion: null
    };
  }
  checks.push({ ...tagsCheck, name: 'Tags', weight: 10 });
  totalScore += (tagsCheck.score / 100) * 10;
  maxScore += 10;
  
  // 8. Featured Image Alt Text Check (weight: 5)
  const hasFeaturedImage = !!post.image_id || !!post.image;
  const hasAltText = !!(post.image_alt_text && post.image_alt_text.trim());
  let altTextCheck;
  if (!hasFeaturedImage) {
    altTextCheck = {
      pass: true,
      score: 100,
      status: 'n/a',
      message: 'No featured image (N/A)',
      suggestion: null
    };
  } else if (hasAltText) {
    altTextCheck = {
      pass: true,
      score: 100,
      status: 'good',
      message: 'Featured image has alt text',
      suggestion: null
    };
  } else {
    altTextCheck = {
      pass: false,
      score: 0,
      status: 'missing',
      message: 'Featured image missing alt text',
      suggestion: 'Add alt text to the featured image'
    };
  }
  checks.push({ ...altTextCheck, name: 'Image Alt Text', weight: 5 });
  totalScore += (altTextCheck.score / 100) * 5;
  maxScore += 5;
  
  // Calculate overall score
  const overallScore = Math.round((totalScore / maxScore) * 100);
  
  // Determine grade
  let grade, gradeColor;
  if (overallScore >= 90) {
    grade = 'Excellent';
    gradeColor = '#48bb78';
  } else if (overallScore >= 70) {
    grade = 'Good';
    gradeColor = '#68d391';
  } else if (overallScore >= 50) {
    grade = 'Needs Work';
    gradeColor = '#ecc94b';
  } else {
    grade = 'Poor';
    gradeColor = '#f56565';
  }
  
  return {
    score: overallScore,
    grade,
    gradeColor,
    checks,
    passedChecks: checks.filter(c => c.pass).length,
    totalChecks: checks.length
  };
};

/**
 * Get status icon for a check
 */
export const getStatusIcon = (status) => {
  switch (status) {
    case 'good':
    case 'excellent':
      return '✓';
    case 'okay':
    case 'partial':
    case 'many':
      return '⚠';
    case 'n/a':
      return '•';
    default:
      return '✗';
  }
};

/**
 * Get status color for a check
 */
export const getStatusColor = (status) => {
  switch (status) {
    case 'good':
    case 'excellent':
      return '#48bb78';
    case 'okay':
    case 'partial':
    case 'many':
      return '#ecc94b';
    case 'n/a':
      return '#a0aec0';
    default:
      return '#f56565';
  }
};
